import { Link, useLocation } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { useSeo } from '@/lib/useSeo';
import { BRAND } from '@/lib/brand';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import MobileCTA from '@/components/site/MobileCTA';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const areaSlug = (a) => a.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const quickLinks = [
  { to: "/", label: "Home" },
  { to: "/bed-bug-treatment-vancouver", label: "Vancouver Guide" },
  { to: "/#guide", label: "The Bed Bug Guide" },
  { to: "/#booking", label: "Book Treatment" },
  { to: "/contact", label: "Contact" },
];

export default function PageNotFound() {
  const location = useLocation();
  useSeo({ robots: "noindex, follow" });

  const { data: authData, isFetched } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      try {
        const user = await base44.auth.me();
        return { user, isAuthenticated: true };
      } catch (error) {
        return { user: null, isAuthenticated: false };
      }
    }
  });

  return (
    <div className="overflow-x-hidden pb-16 lg:pb-0">
      <Navbar />
      <main id="main-content" className="bg-background px-5 pt-32 pb-24 lg:px-8 lg:pt-44 lg:pb-32">
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">404 · Page Not Found</span>
          <h1 className="mt-6 font-heading text-5xl font-extrabold leading-[1.02] tracking-tight text-primary sm:text-6xl">That page slipped past our perimeter.</h1>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            The page <span className="font-medium text-foreground">&ldquo;{location.pathname}&rdquo;</span> could not be found. It may have moved or never existed — here&rsquo;s where to go next.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="h-12 px-8 bg-accent text-white hover:bg-accent/90">
              <Link to="/"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Home</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 px-8">
              <Link to="/bed-bug-treatment-vancouver">Bed Bug Treatment Vancouver</Link>
            </Button>
          </div>
          <div className="mx-auto mt-12 flex max-w-xl flex-wrap items-center justify-center gap-2">
            {quickLinks.map((l) => (
              <Link key={l.label} to={l.to} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{l.label}</Link>
            ))}
          </div>
          <div className="mx-auto mt-8 flex max-w-2xl flex-wrap items-center justify-center gap-2">
            {BRAND.areas.map((a) => (
              <Link key={a} to={a === "Vancouver" ? "/bed-bug-treatment-vancouver" : `/bed-bug-treatment/${areaSlug(a)}`} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{a}</Link>
            ))}
          </div>
          {isFetched && authData.isAuthenticated && authData.user?.role === 'admin' && (
            <div className="mx-auto mt-12 max-w-md rounded-lg border border-border bg-muted/30 p-4 text-left text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Admin note</p>
              <p className="mt-1">This route may not be implemented yet — ask the AI to build it in the chat.</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <MobileCTA />
    </div>
  );
}